// /admin/communications — unified inbox across repairs + tenancies. Each
// conversation row is grouped by its anchor (repair_id OR tenancy_id) and
// shows the latest message preview. Click a row to open the thread + composer.

import { MessageComposer } from "@/components/MessageComposer";
import { type Anchor, MessageThread } from "@/components/MessageThread";
import { Card, CardContent } from "@/components/ui/card";
import { formatDateGB } from "@/lib/format";
import { getSupabase } from "@/lib/supabase";
import { ChevronRight, Mail, Wrench } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

type RepairMsg = {
  id: string;
  repair_id: string | null;
  tenancy_id: string | null;
  direction: "in" | "out";
  sender_email: string;
  sender_name: string | null;
  sender_role: "admin" | "tenant" | "contractor" | "agent" | "system";
  body_text: string | null;
  source: string;
  created_at: string;
};

type Conversation = {
  anchor: Anchor;
  title: string;
  sub: string;
  lastMessage: RepairMsg;
  lastSenderLabel: string;
  unreadCount: number;
};

type Filter = "all" | "tenants" | "repairs" | "unclassified";

export function AdminCommunications() {
  const [convos, setConvos] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Conversation | null>(null);
  const [filter, setFilter] = useState<Filter>("all");

  useEffect(() => {
    let cancelled = false;
    const sb = getSupabase();

    // Build the conversation list. Used on mount + on every realtime INSERT
    // (debounced) so the UI stays in sync without flickering.
    async function refresh(showSpinner: boolean) {
      if (showSpinner) setLoading(true);
      setError(null);
      const { data, error: fetchErr } = await sb
        .from("repair_messages")
        .select(`
          id, repair_id, tenancy_id, direction, sender_email, sender_name,
          sender_role, body_text, source, created_at,
          repair:repairs(id, title, tenancy:tenancies(property:properties(display_name))),
          tenancy:tenancies(id, property:properties(display_name))
        `)
        .order("created_at", { ascending: false })
        .limit(500);

      const { data: notifData } = await sb
        .from("admin_notifications")
        .select("repair_message_id, read_at")
        .is("read_at", null);
      const unreadSet = new Set(
        (notifData ?? []).map((n: { repair_message_id: string }) => n.repair_message_id),
      );

      if (cancelled) return;
      if (fetchErr) {
        setError(fetchErr.message);
        setLoading(false);
        return;
      }
      const seen = new Map<string, Conversation>();
      const unreadByAnchor = new Map<string, number>();
      for (const row of (data ?? []) as Array<Record<string, unknown> & RepairMsg>) {
        const anchorKey = row.repair_id
          ? `repair:${row.repair_id}`
          : row.tenancy_id
            ? `tenancy:${row.tenancy_id}`
            : null;
        if (!anchorKey) continue;
        if (unreadSet.has(row.id)) {
          unreadByAnchor.set(anchorKey, (unreadByAnchor.get(anchorKey) ?? 0) + 1);
        }
        if (seen.has(anchorKey)) continue;

        type EnrichedRow = RepairMsg & {
          repair?: {
            title?: string | null;
            tenancy?: { property?: { display_name?: string | null } | null } | null;
          } | null;
          tenancy?: { property?: { display_name?: string | null } | null } | null;
        };
        const r = row as EnrichedRow;
        let title = "Conversation";
        let sub = "";
        let anchor: Anchor;
        if (r.repair_id) {
          anchor = { kind: "repair", id: r.repair_id };
          title = r.repair?.title ?? "Repair";
          sub = r.repair?.tenancy?.property?.display_name ?? "";
        } else if (r.tenancy_id) {
          anchor = { kind: "tenancy", id: r.tenancy_id };
          const prop = r.tenancy?.property?.display_name ?? "Tenancy";
          title = prop;
          sub = "General correspondence";
        } else {
          continue;
        }
        seen.set(anchorKey, {
          anchor,
          title,
          sub,
          lastMessage: row as RepairMsg,
          lastSenderLabel: r.sender_name?.trim() || r.sender_email,
          unreadCount: 0,
        });
      }
      const list = Array.from(seen.entries()).map(([key, c]) => ({
        ...c,
        unreadCount: unreadByAnchor.get(key) ?? 0,
      }));
      setConvos(list);
      if (showSpinner) setLoading(false);
    }

    void refresh(true);

    // Realtime: any new message triggers a quiet refetch. Debounce so burst
    // INSERTs (e.g. webhook backfills) coalesce into one fetch instead of
    // thrashing the list.
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;
    const channel = sb
      .channel("admin-comms")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "repair_messages" },
        () => {
          if (cancelled) return;
          if (debounceTimer) clearTimeout(debounceTimer);
          debounceTimer = setTimeout(() => {
            if (!cancelled) void refresh(false);
          }, 500);
        },
      )
      .subscribe();
    return () => {
      cancelled = true;
      if (debounceTimer) clearTimeout(debounceTimer);
      void sb.removeChannel(channel);
    };
  }, []);

  // When the selected conversation changes, mark its unread notifications
  // as read for this admin.
  useEffect(() => {
    if (!selected) return;
    const sb = getSupabase();
    (async () => {
      const column = selected.anchor.kind === "repair" ? "repair_id" : "tenancy_id";
      const { data: msgs } = await sb
        .from("repair_messages")
        .select("id")
        .eq(column, selected.anchor.id);
      const ids = (msgs ?? []).map((m: { id: string }) => m.id);
      if (ids.length === 0) return;
      const { data: caller } = await sb.auth.getUser();
      if (!caller?.user?.id) return;
      await sb
        .from("admin_notifications")
        .update({ read_at: new Date().toISOString() })
        .eq("admin_user_id", caller.user.id)
        .in("repair_message_id", ids)
        .is("read_at", null);
    })();
  }, [selected]);

  const filtered = useMemo(() => {
    if (filter === "all") return convos;
    if (filter === "repairs") return convos.filter((c) => c.anchor.kind === "repair");
    if (filter === "tenants") return convos.filter((c) => c.anchor.kind === "tenancy");
    // unclassified: messages with no anchor metadata yet — none in current shape
    return convos.filter((c) => !c.title || c.title === "Conversation");
  }, [convos, filter]);

  return (
    <div className="flex flex-col gap-4 max-w-6xl mx-auto w-full">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">Communications</h1>
          <p className="text-sm text-ink-dim mt-1">
            Every email and portal message across the portfolio. Click a conversation to read or
            reply.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {(["all", "tenants", "repairs", "unclassified"] as Filter[]).map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setFilter(f)}
              className={`text-xs px-3 py-1 rounded-full border transition capitalize ${
                filter === f
                  ? "bg-accent text-accent-on border-accent"
                  : "bg-bg-card text-ink-dim border-line hover:text-ink"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {error ? (
        <p role="alert" className="text-xs text-red-300">
          {error}
        </p>
      ) : null}

      <div className="grid grid-cols-1 md:grid-cols-[minmax(0,360px)_1fr] gap-4">
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <p className="text-sm text-ink-dim p-4">Loading…</p>
            ) : filtered.length === 0 ? (
              <p className="text-sm text-ink-dim p-6 text-center">No conversations yet.</p>
            ) : (
              <ul className="divide-y divide-line max-h-[70vh] overflow-y-auto">
                {filtered.map((c) => {
                  const key = `${c.anchor.kind}:${c.anchor.id}`;
                  const selectedKey = selected
                    ? `${selected.anchor.kind}:${selected.anchor.id}`
                    : null;
                  const isActive = selectedKey === key;
                  const Icon = c.anchor.kind === "repair" ? Wrench : Mail;
                  return (
                    <li key={key}>
                      <button
                        type="button"
                        onClick={() => setSelected(c)}
                        className={`w-full text-left px-4 py-3 flex items-start gap-3 transition ${
                          isActive ? "bg-accent/10" : "hover:bg-white/5"
                        }`}
                      >
                        <Icon size={14} className="mt-0.5 shrink-0 text-ink-dim" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium truncate">{c.title}</span>
                            {c.unreadCount > 0 ? (
                              <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-md bg-amber-500/20 text-amber-300 border border-amber-500/30">
                                {c.unreadCount}
                              </span>
                            ) : null}
                            <span className="ml-auto text-[10px] text-ink-dim shrink-0">
                              {formatDateGB(c.lastMessage.created_at)}
                            </span>
                          </div>
                          {c.sub ? (
                            <p className="text-[11px] text-ink-dim mt-0.5 truncate">{c.sub}</p>
                          ) : null}
                          <p className="text-xs text-ink-dim mt-1 line-clamp-2">
                            <span className="font-medium text-ink">{c.lastSenderLabel}:</span>{" "}
                            {(c.lastMessage.body_text ?? "").slice(0, 200)}
                          </p>
                        </div>
                        <ChevronRight size={14} className="text-ink-dim mt-1 shrink-0" />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </CardContent>
        </Card>

        <div className="flex flex-col gap-4 min-w-0">
          {selected ? (
            <>
              <div>
                <h2 className="text-lg font-semibold truncate">{selected.title}</h2>
                <p className="text-xs text-ink-dim">{selected.sub}</p>
              </div>
              <MessageThread
                anchor={selected.anchor}
                title={selected.anchor.kind === "repair" ? "Repair thread" : "Tenancy thread"}
                description={
                  selected.anchor.kind === "repair"
                    ? "Email + portal messages for this repair."
                    : "Email + portal messages with this tenant (non-repair)."
                }
              />
              <MessageComposer anchor={selected.anchor} />
            </>
          ) : (
            <Card>
              <CardContent>
                <p className="text-sm text-ink-dim py-8 text-center">
                  Pick a conversation on the left.
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
